<script setup lang="ts">
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "~/layers/ui/components/avatar";

import Button from "~/layers/ui/components/button/Button.vue";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "~/layers/ui/components/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
} from "~/layers/ui/components/sidebar";
import {
  Banknote,
  ChevronsUpDown,
  LockOpen,
  FlaskConical,
  LogOut,
  Megaphone,
  PieChart,
  Search,
  Users,
  MessageSquareText,
  PlusCircle,
  Building2,
} from "lucide-vue-next";
import { ref, watch } from "vue";
import { useAuth } from "../layers/auth/composables/useAuth";
import type { RouterOutput } from "~/plugins/trpc";
import { ConfirmationDialog } from "~/layers/ui/components/confirmation-dialog";
import { useUserStore } from "~/stores/userStore";
import {
  hasAdvertiserPrivilege,
  hasGlobalPrivilege,
} from "evangelist-admin/permissions";
import { AdminappPrivilege } from "evangelist-db-schema/api";
import { useNavigationStore } from "~/stores/navigationStore";
import Toaster from "~/layers/ui/components/toast/Toaster.vue";
import EvangelistLogo from "~/layers/ui/components/evangelist-logo/EvangelistLogo.vue";
import LookUpCommand from "~/layers/support/components/look-up-command.vue";
import { NuxtLink } from "#components";

const { logout } = useAuth();

const userStore = useUserStore();
const { $trpc } = useNuxtApp();
const navigationStore = useNavigationStore();
const runtimeConfig = useRuntimeConfig();

const opened = ref(true);

const advertisers = ref<RouterOutput["advertiser"]["listAdvertisers"]>([]);

watch(
  () => userStore.user,
  (user) => {
    if (!user) {
      advertisers.value = [];
      return;
    }

    $trpc.advertiser.listAdvertisers.query().then((res) => {
      advertisers.value = res;
      if (
        res.length === 1 &&
        navigationStore.activeAdvertiser?.advertiserId !== res[0].advertiserId
      ) {
        navigationStore.activeAdvertiser = res[0];
      }

      // remove advertiser from navigationStore if it is no longer visible for user
      if (
        navigationStore.activeAdvertiser &&
        !advertisers.value.some(
          ({ advertiserId }) =>
            advertiserId === navigationStore.activeAdvertiser?.advertiserId,
        )
      ) {
        navigationStore.activeAdvertiser = null;
      }
    });
  },
  { immediate: true },
);

watch(
  () => navigationStore.activeAdvertiser,
  () => {
    navigateTo(`/campaigns/`);
  },
);

const showTeamManagement = computed(
  () =>
    navigationStore.activeAdvertiser &&
    userStore.user &&
    hasAdvertiserPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.UpdateAdvertiserMember,
      navigationStore.activeAdvertiser.advertiserId,
    ),
);

const showReporting = computed(() => navigationStore.activeAdvertiser);

const canViewGlobalReports = computed(
  () =>
    userStore.user &&
    hasGlobalPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.ViewGlobalReports,
    ),
);

const showGlobalReporting = computed(() => !navigationStore.activeAdvertiser);

/* const canAddAdvertiser = computed(
  () =>
    userStore.user &&
    hasGlobalPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.CreateAdvertiser,
    ),
); */

const canUseSupportTools = computed(
  () =>
    userStore.user &&
    hasGlobalPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.UseSupportTools,
    ),
);

const canListWithdrawals = computed(
  () =>
    userStore.user &&
    hasGlobalPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.ListWithdrawals,
    ),
);

const canListUsers = computed(
  () =>
    userStore.user &&
    hasGlobalPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.ListUsers,
    ),
);

const canSendMessages = computed(
  () =>
    userStore.user &&
    navigationStore.activeAdvertiser?.advertiserId &&
    hasAdvertiserPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.SendDirectMessage,
      navigationStore.activeAdvertiser.advertiserId,
    ),
);

const canListAmbassadors = computed(
  () =>
    userStore.user &&
    navigationStore.activeAdvertiser?.advertiserId &&
    hasAdvertiserPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.ListAmbassadors,
      navigationStore.activeAdvertiser.advertiserId,
    ),
);

const canObtainSuperpowers = computed(
  () =>
    userStore.user &&
    hasGlobalPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.ObtainSuperpowers,
    ),
);

const canManageAdvertiserProfile = computed(
  () =>
    userStore.user &&
    navigationStore.activeAdvertiser?.advertiserId &&
    hasAdvertiserPrivilege(
      userStore.user.userRoleAssignments,
      AdminappPrivilege.CanManageAdvertiserProfile,
      navigationStore.activeAdvertiser.advertiserId,
    ),
);

async function stopSpoofing() {
  await $trpc.admin.stopSpoofing.mutate();
  window.location.reload();
}

const nameInitials = computed(() => {
  if (!userStore.user || !userStore.user.name) {
    return "";
  }

  const name = userStore.user.name;
  const initials = name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

  return initials;
});

useHead({ titleTemplate: "%s | Evangelist Admin" });
</script>

<template>
  <div>
    <Toaster />
    <ConfirmationDialog />
    <SidebarProvider @update:open="opened = $event">
      <Sidebar class="border-gray-300" collapsible="icon">
        <SidebarHeader class="bg-[#eeeeee]">
          <SidebarMenu>
            <SidebarMenuItem>
              <div
                class="px-1 flex items-center justify-between mt-3"
                :class="opened ? 'px-2' : 'px-1'"
              >
                <NuxtLink class="flex gap-2" href="/">
                  <EvangelistLogo />
                  <p v-if="opened" class="font-semibold">Evangelist</p>
                </NuxtLink>

                <div v-if="opened" class="relative">
                  <p class="text-[#6659FF] text-sm font-semibold">Admin app</p>

                  <div
                    v-if="runtimeConfig.public.ENV_NAME !== 'prod'"
                    class="absolute text-xs font-semibold -top-5 -right-3 mt-0.5 border border-gray-200 shadow-sm bg-white px-1.5 py-0.5 rounded-lg"
                  >
                    <div>
                      {{ runtimeConfig.public.ENV_NAME.toUpperCase() }}
                    </div>
                  </div>
                </div>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>

          <LookUpCommand v-if="canUseSupportTools && opened" class="mt-4" />
        </SidebarHeader>
        <SidebarContent
          class="bg-[#eeeeee]"
          :class="[canUseSupportTools ? 'pt-0' : 'pt-4']"
        >
          <SidebarGroup class="group-data-[collapsible=icon]:hidden">
            <p
              v-if="advertisers.length === 0"
              class="text-gray-600 text-sm px-2"
            >
              {{
                navigationStore.activeAdvertiser
                  ? navigationStore.activeAdvertiser.name
                  : "Advertisers"
              }}
            </p>

            <p
              v-if="advertisers.length === 1"
              class="text-gray-600 text-sm px-2"
            >
              {{
                navigationStore.activeAdvertiser
                  ? navigationStore.activeAdvertiser.name
                  : ""
              }}
            </p>

            <SidebarMenu v-if="advertisers.length > 1">
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger as-child>
                    <SidebarMenuButton
                      v-if="navigationStore.activeAdvertiser"
                      size="lg"
                      class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground h-auto"
                    >
                      <div
                        class="grid flex-1 py-0 text-left text-gray-600 text-sm font-medium leading-tight"
                      >
                        <span class="truncate font-medium">{{
                          navigationStore.activeAdvertiser?.name
                        }}</span>
                      </div>
                      <ChevronsUpDown class="ml-auto" />
                    </SidebarMenuButton>
                    <SidebarMenuButton
                      v-else
                      size="lg"
                      class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    >
                      <div class="grid flex-1 text-left text-sm leading-tight">
                        <span class="truncate font-semibold"
                          >All advertisers</span
                        >
                      </div>
                      <ChevronsUpDown class="ml-auto" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                    align="start"
                    side="bottom"
                    :side-offset="4"
                  >
                    <DropdownMenuLabel class="text-xs text-muted-foreground">
                      Advertisers
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      v-if="advertisers.length > 1"
                      class="gap-2 p-2"
                      @click="navigationStore.activeAdvertiser = null"
                    >
                      All advertisers
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      v-for="advertiser in advertisers"
                      :key="advertiser.name"
                      class="gap-2 p-2"
                      @click="navigationStore.activeAdvertiser = advertiser"
                    >
                      <div
                        class="flex size-6 items-center justify-center rounded-sm"
                      >
                        <img
                          height="24"
                          width="24"
                          class="shring-0 w-6 h-6"
                          :src="
                            runtimeConfig.public.ASSETS_URL +
                            '/assets/' +
                            advertiser.theme +
                            '/logo-sm.svg'
                          "
                        />
                      </div>
                      {{ advertiser.name }}
                    </DropdownMenuItem>
                    <!--     <DropdownMenuSeparator v-if="canAddAdvertiser" />
                    <DropdownMenuItem v-if="canAddAdvertiser" class="gap-2 p-2">
                      <div
                        class="flex size-6 items-center justify-center rounded-md border bg-background"
                      >
                        <Plus class="size-4" />
                      </div>
                      <div class="font-medium text-muted-foreground">
                        Add advertiser
                      </div>
                    </DropdownMenuItem> -->
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton as-child>
                  <NuxtLink href="/campaigns">
                    <component :is="Megaphone" />
                    <span>Campaigns</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem v-if="showReporting">
                <SidebarMenuButton as-child>
                  <NuxtLink href="/reporting">
                    <component :is="PieChart" />
                    <span>Reporting</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem
                v-if="canViewGlobalReports && showGlobalReporting"
              >
                <SidebarMenuButton as-child>
                  <NuxtLink href="/global-reports">
                    <component :is="PieChart" />
                    <span>Global Reports</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem v-if="canListAmbassadors">
                <SidebarMenuButton as-child>
                  <NuxtLink href="/ambassadors">
                    <component :is="Users" />
                    <span>Ambassadors</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem v-if="canSendMessages">
                <SidebarMenuButton as-child>
                  <NuxtLink href="/messages/direct-messages">
                    <component :is="MessageSquareText" />
                    <span>Direct Messages</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem v-if="showTeamManagement">
                <SidebarMenuButton as-child>
                  <NuxtLink href="/team-management">
                    <Users />
                    Team Management
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem v-if="canManageAdvertiserProfile">
                <SidebarMenuButton as-child>
                  <NuxtLink href="/company-profile">
                    <component :is="Building2" />
                    <span>Company Profile</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>

            <SidebarMenu v-if="canUseSupportTools" class="mt-8">
              <p class="text-gray-600 text-sm px-2">Global Support Tools</p>
              <SidebarMenuItem class="mt-1">
                <SidebarMenuButton as-child>
                  <NuxtLink href="/search">
                    <component :is="Search" />
                    <span>Look up</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton as-child>
                  <NuxtLink href="/discovery">
                    <component :is="PlusCircle" />
                    <span>Submit Tweet</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem v-if="canListWithdrawals">
                <SidebarMenuButton as-child>
                  <NuxtLink href="/withdrawals">
                    <component :is="Banknote" />
                    <span>Withdrawals</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem v-if="canListUsers">
                <SidebarMenuButton as-child>
                  <NuxtLink href="/users">
                    <component :is="Users" />
                    <span>Admin Users</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem
                v-if="
                  canObtainSuperpowers &&
                  runtimeConfig.public.ENV_NAME !== 'stage' &&
                  runtimeConfig.public.ENV_NAME !== 'prod'
                "
              >
                <SidebarMenuButton as-child>
                  <NuxtLink href="/test-helpers">
                    <FlaskConical class="size-4" />
                    <span>Test Helpers</span>
                  </NuxtLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter class="bg-[#eeeeee]">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger as-child>
                  <SidebarMenuButton
                    size="lg"
                    class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    <Avatar class="h-8 w-8 rounded-lg">
                      <AvatarImage src="" :alt="userStore.user?.name" />
                      <AvatarFallback class="rounded-lg">
                        {{ nameInitials }}
                      </AvatarFallback>
                    </Avatar>
                    <div class="grid flex-1 text-left text-sm leading-tight">
                      <span class="truncate font-semibold">{{
                        userStore.user?.name
                      }}</span>
                      <span class="truncate text-xs">{{
                        userStore.user?.email
                      }}</span>
                    </div>
                    <ChevronsUpDown class="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                  side="bottom"
                  align="end"
                  :side-offset="4"
                >
                  <DropdownMenuLabel class="p-0 font-normal">
                    <div
                      class="flex items-center gap-2 px-1 py-1.5 text-left text-sm"
                    >
                      <Avatar class="h-8 w-8 rounded-lg">
                        <AvatarImage src="" :alt="userStore.user?.name" />
                        <AvatarFallback class="rounded-lg">
                          {{ nameInitials }}
                        </AvatarFallback>
                      </Avatar>
                      <div class="grid flex-1 text-left text-sm leading-tight">
                        <span class="truncate font-semibold">{{
                          userStore.user?.name
                        }}</span>
                        <span class="truncate text-xs">{{
                          userStore.user?.email
                        }}</span>
                      </div>
                    </div>
                  </DropdownMenuLabel>

                  <DropdownMenuItem
                    v-if="canObtainSuperpowers"
                    class="my-2 hover:!text-red-600 hover:!bg-red-50"
                    @click="userStore.activateSuperpowers()"
                  >
                    <LockOpen :size="20" />
                    Enter Superpowers mode
                  </DropdownMenuItem>

                  <DropdownMenuItem @click="logout">
                    <LogOut />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <main class="px-4 sm:px-8 pb-8 py-2 min-h-screen bg-[#eeeeee]">
          <slot />

          <div
            v-if="userStore.hasSuperpowers"
            class="fixed flex items-center justify-between gap-4 bottom-4 z-[99999] px-3 py-2 bg-red-100 border rounded-lg border-red-500 w-[calc(100vw-50px)] sm:w-[calc(100vw-300px)] font-bold left-68"
          >
            <p class="text-gray-800">
              Superpower mode activated for:
              {{ userStore.formattedRemainingSuperpowerTime }}
            </p>

            <div class="flex gap-4">
              <Button
                class="secondary"
                @click="userStore.activateSuperpowers()"
              >
                Extend
              </Button>
              <Button class="secondary" @click="userStore.disableSuperpowers()">
                Exit Superpower Mode
              </Button>
            </div>
          </div>
          <div
            v-if="userStore.isSpoofing"
            class="fixed flex items-center justify-between gap-4 bottom-4 z-[99999] px-3 py-2 bg-red-100 border rounded-lg border-red-500 w-[calc(100vw-50px)] sm:w-[calc(100vw-300px)] font-bold left-68"
          >
            <p class="text-gray-800">Acting as: {{ userStore.user?.name }}</p>

            <Button class="secondary" @click="stopSpoofing">
              Back to my account</Button
            >
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  </div>
</template>
